import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import PageHero from '../components/PageHero';
import ProductCard from '../components/ProductCard';
import CategoryGrid from '../components/CategoryGrid';
import CtaPanel from '../components/CtaPanel';
import { brands } from '../data/brands';
import { products } from '../data/products';
import { categories } from '../data/categories';
import { quotePath } from '../utils/contactLinks';
import './Products.css';

const ADVANTAGES = [
  {
    icon: 'leaf',
    title: 'Green Building Expertise',
    text: 'Our owner is an accredited Green Building Council Professional who can advise on energy-efficient product choices.',
  },
  {
    icon: 'layers',
    title: 'Master Distributor',
    text: 'Direct distribution for SuperTec, ETEX, Siniat and OWA means better pricing and stock availability.',
  },
  {
    icon: 'award',
    title: '30+ Years in the Industry',
    text: 'Led by Niel Kasselman, well-known in the building industry with hands-on expertise to guide your product selection.',
  },
  {
    icon: 'grid',
    title: 'Multi-Brand Range',
    text: 'Also distributing products from KNAUF, CAPCO, Casofour, Isover, Gyproc, RhinoROC, WoodUpp, Youngman, Isotherm, Nutec, MecoLadder, Summit, Aerolite, iTe and Swartland — one stop for all your building material needs.',
  },
];

function Products() {
  const location = useLocation();
  const hashCategory = location.hash?.slice(1) || 'all';
  const selectedCategory = ['all', ...categories.map((c) => c.id)].includes(hashCategory) ? hashCategory : 'all';

  const allCategories = [{ id: 'all', label: 'All Products' }, ...categories];
  const filteredProducts =
    selectedCategory === 'all' ? products : products.filter((p) => p.categoryId === selectedCategory);

  const brandNameList = brands.map((b) => b.shortName || b.name).join(', ');

  useEffect(() => {
    if (!location.hash) return;
    const id = setTimeout(() => {
      const el = document.getElementById('full-range');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(id);
  }, [location.pathname, location.hash]);

  return (
    <div className="products-page">
      <SEO />

      <PageHero
        breadcrumbs={[{ name: 'Products', path: '/products' }]}
        kicker="In stock in George"
        bg="/images/bg-acoustic.jpg"
        title="Ceiling, Drywall & Insulation Products"
        text={`Ceiling boards and tiles, suspended ceiling grid, plasterboard, drywall partitioning, insulation, acoustic panels, plasters, ladders, door frames, tools and fasteners — in stock at our George warehouse, from trusted manufacturers including ${brandNameList}.`}
        actions={
          <>
            <Link to={quotePath} className="btn btn-sky btn-large">
              Request a quote <Icon name="arrow" />
            </Link>
            <a href="#full-range" className="btn btn-ghost btn-large">
              See the full range
            </a>
          </>
        }
      />

      <section className="section section-white">
        <div className="container">
          <div className="section-head">
            <span className="pill">Shop by category</span>
            <h2 className="section-title">Choose a product range</h2>
          </div>
          <CategoryGrid />
        </div>
      </section>

      <section id="full-range" className="section section-light">
        <div className="container">
          <div className="section-head">
            <span className="pill">Full range</span>
            <h2 className="section-title">Every product, features &amp; brands</h2>
          </div>

          <div className="category-filter" role="tablist" aria-label="Filter products">
            {allCategories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                role="tab"
                aria-selected={selectedCategory === cat.id}
                className={`category-btn${selectedCategory === cat.id ? ' active' : ''}`}
              >
                {cat.label}
              </a>
            ))}
          </div>

          <div className="products-grid">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-white">
        <div className="container">
          <div className="section-head">
            <span className="pill">Why buy from us</span>
            <h2 className="section-title">The OJANNA Ceiling &amp; Drywall advantage</h2>
          </div>
          <div className="grid grid-4">
            {ADVANTAGES.map((a) => (
              <div key={a.title} className="card">
                <div className="card-icon">
                  <Icon name={a.icon} />
                </div>
                <h3 className="card-title">{a.title}</h3>
                <p className="card-text">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaPanel />
    </div>
  );
}

export default Products;
